import _ from 'lodash';
import { BlockEmbed } from 'quill/blots/block';
import Quill, { AttributeMap, Delta } from 'quill/core';
import React from 'react';
import type { CarouselItem } from '../../formats/ImageBlot';
import type ImageBlot from '../../formats/ImageBlot';
import { compactCarouselItem } from '../../formats/ImageBlot';
import type { HoveredBlotListenerSetting } from '../../HoveredBlotListener';
import { useHoveredBlotListener } from '../../HoveredBlotListener';
import { useQuill } from '../../QuillContext';
import type { Blot } from '../../types';
import {
  imageLeftIcon,
  imageRightIcon,
  spanDefaultIcon,
  spanFullIcon,
  spanHalfIcon,
} from '../editorIcons';
import ToolbarButton, { ToolbarButtonGroup } from './buttons/ToolbarButton';
import ToolbarCommentButtonGroup from './buttons/ToolbarCommentButtonGroup';
import ToolbarEditButton from './buttons/ToolbarEditButton';
import FloatingToolbar from './FloatingToolbar';
import useBlotHighlightedClass from './useBlotHighlightedClass';

type Formats = Record<string, string[] | string | null | undefined> & {
  comment?: string[] | null;
};

type UpdateImage = (change: {
  width?: number | null;
  attributes: AttributeMap;
}) => void;

interface BaseImageToolbarProps {
  hoveredBlot?: Blot;
  formats: Formats;
  firstImageWidth?: number | null;
  updateImage: UpdateImage;
  readOnly: boolean;
  naturalWidth: number;
  setPersist?: (persist: boolean) => void;
}

export const BaseImageToolbar = ({
  hoveredBlot,
  formats,
  firstImageWidth,
  updateImage,
  naturalWidth,
  setPersist,
  readOnly,
}: BaseImageToolbarProps) => {
  useBlotHighlightedClass(hoveredBlot);

  return (
    <>
      {readOnly ? (
        <ToolbarButtonGroup>
          <ToolbarEditButton targetBlot={hoveredBlot} />
        </ToolbarButtonGroup>
      ) : (
        <>
          <ToolbarButtonGroup>
            <ToolbarButton
              icon={spanHalfIcon}
              tooltip="Half width"
              active={formats.span === 'half'}
              onClick={() =>
                updateImage({
                  width: naturalWidth / 2,
                  attributes: { span: 'half', float: null },
                })
              }
              testId="imageHalfWidthButton"
            />
            <ToolbarButton
              icon={spanDefaultIcon}
              tooltip="Default width"
              active={
                !formats.span && firstImageWidth == null && !formats.float
              }
              onClick={() => {
                updateImage({
                  width: null,
                  attributes: { span: null, float: null },
                });
              }}
              testId="imageDefaultWidthButton"
            />
            <ToolbarButton
              icon={spanFullIcon}
              tooltip="Full width"
              active={formats.span === 'full'}
              onClick={() =>
                updateImage({
                  width: null,
                  attributes: { span: 'full', float: null },
                })
              }
              testId="imageFullWidthButton"
            />
          </ToolbarButtonGroup>
          <ToolbarButtonGroup>
            <ToolbarButton
              icon={imageLeftIcon}
              tooltip="Float Left"
              active={formats.float === 'left'}
              onClick={() =>
                updateImage({
                  attributes: { span: null, float: 'left' },
                })
              }
              testId="imageLeftFloatButton"
            />
            <ToolbarButton
              icon={imageRightIcon}
              tooltip="Float Right"
              active={formats.float === 'right'}
              onClick={() =>
                updateImage({
                  attributes: { span: null, float: 'right' },
                })
              }
            />
          </ToolbarButtonGroup>
        </>
      )}
      <ToolbarCommentButtonGroup
        blot={hoveredBlot}
        existingComment={formats.comment}
        onReactionPickerOpenChange={setPersist}
      />
    </>
  );
};

type Context = {
  firstImageWidth?: number;
  naturalWidth?: number;
  formats: Record<string, string[] | string | null | undefined>;
};

const hoveredBlotListenerSetting: HoveredBlotListenerSetting<Context> = {
  getHoveredBlot(blot: Blot, { node }) {
    return node instanceof Element &&
      node.tagName === 'IMG' &&
      blot.statics.blotName === 'image'
      ? blot
      : null;
  },
  getContext(blot: ImageBlot) {
    const img = blot.domNode.querySelector('img');
    const value = blot.value().image;
    const firstImageWidth = Array.isArray(value) ? value[0]?.width : undefined;
    return {
      firstImageWidth,
      formats: blot.formats(),
      naturalWidth: img?.naturalWidth,
    };
  },
};

const checkIsCarouseItems = (
  imageValue: unknown,
): imageValue is CarouselItem[] => Array.isArray(imageValue);

const ImageToolbar = () => {
  const { quill, readOnly } = useQuill();

  const {
    bounds,
    hoveredBlot,
    setPersist,
    context: { firstImageWidth, naturalWidth, formats } = {
      firstImageWidth: undefined,
      naturalWidth: undefined,
      formats: undefined,
    },
  } = useHoveredBlotListener(quill, hoveredBlotListenerSetting);

  const updateImage: UpdateImage = (change) => {
    if (!(hoveredBlot instanceof BlockEmbed)) return;
    const delta = new Delta().retain(hoveredBlot.offset(hoveredBlot.scroll));
    let retain: 1 | { image: CarouselItem[] } = 1;
    const currentFormats = hoveredBlot.formats();
    const attributes = AttributeMap.diff(
      currentFormats,
      AttributeMap.compose(currentFormats, change.attributes, false),
    );

    if (typeof change.width !== 'undefined') {
      const value = hoveredBlot.value().image;
      if (checkIsCarouseItems(value)) {
        const firstImage = value[0];
        if (firstImage) {
          retain = {
            image: [
              compactCarouselItem({
                ...firstImage,
                width: change.width ?? undefined,
              }),
              ...value.slice(1),
            ],
          };
        }
      }
    }
    if (retain !== 1 || !_.isEmpty(attributes)) {
      quill?.updateContents(
        delta.retain(retain, attributes),
        Quill.sources.USER,
      );
    }
  };

  return (
    <FloatingToolbar leftAligned={formats?.float === 'left'} bounds={bounds}>
      {hoveredBlot && naturalWidth != null && formats && (
        <BaseImageToolbar
          hoveredBlot={hoveredBlot}
          formats={formats}
          firstImageWidth={firstImageWidth}
          updateImage={updateImage}
          naturalWidth={naturalWidth}
          setPersist={setPersist}
          readOnly={readOnly}
        />
      )}
    </FloatingToolbar>
  );
};

export default ImageToolbar;
